import { api, apiWithLoading, apiBackground } from './api'
import type { DerivedContactStatus } from '../utils/derivedContactStatus'
import type { ContactProfileFields } from '../types/contactImport'

/**
 * #208 b — les dix cases de la FICHE ENRICHIE (contrat
 * `ContactProfileFields`), rendues par les listes et la fiche, acceptées à la
 * création et à la modification. `name` reste la colonne lue par la
 * téléphonie : le serveur la recalcule « Prénom Nom » dès que prénom et/ou
 * nom sont renseignés.
 */
export type ContactProfile = Partial<ContactProfileFields>

export interface Contact extends ContactProfile {
  id: string
  name: string
  phone: string
  email?: string
  company?: string
  group?: string
  userId?: string // User (not agent) assigned to call this contact
  user?: {
    id: string
    email: string
    name?: string
    role: string
  }
  
  // ✅ NOUVEAU: Données de campagne (status, notes, etc. sont maintenant ici)
  campaignContacts?: Array<{
    campaignId: string
    status: 'NEW' | 'CONTACTED' | 'INTERESTED' | 'NOT_INTERESTED' | 'CUSTOMER'
    notes?: string
    lastCallDate?: string
    nextCallDate?: string
    outcome: string
    priority: number
    callCount: number
    donationAmount?: number
    pledgedAmount?: number
    group?: string
    assignedUserId?: string
    /** Renvoyé par le mode curseur (`search`) uniquement. */
    campaign?: { id: string; name: string }
  }>
  
  createdAt: string
  updatedAt: string
  
  // ⚠️ DEPRECATED: Ces champs existent pour backward compatibility mais utilisez campaignContacts[0] à la place
  notes?: string
  status?: 'NEW' | 'CONTACTED' | 'INTERESTED' | 'NOT_INTERESTED' | 'CUSTOMER'
  lastCallAt?: string
  nextCallAt?: string
  campaignId?: string
}

export interface CreateContactDto extends ContactProfile {
  /** Facultatif dès qu'un prénom ou un nom est donné (#208 b). */
  name?: string
  phone: string
  email?: string
  company?: string
  // REMOVED: status, campaignId, nextCallAt - now in CampaignContact
  userId?: string // User to assign this contact to
}

export interface UpdateContactDto extends Partial<CreateContactDto> {}

/**
 * Agrégats DÉRIVÉS du réel (LOT-CONTACTS-REFONTE) — calculés par le backend
 * depuis campaign_contacts, mutuellement exclusifs, leur somme vaut `total`.
 * Voir le mapping dans `utils/derivedContactStatus.ts`.
 */
export interface ContactStats {
  total: number
  neverCalled: number
  calledNoAnswer: number
  callbackScheduled: number
  interested: number
  notInterested: number
  /** STATUTS-1 — sortie de la file au dernier essai sans réponse. */
  unreachable: number
  /** STATUTS-2 — fiche qualifiée « Mauvais numéro ». */
  wrongNumber: number
  customer: number
}

/** Paramètres du mode curseur de `GET /contacts` (`paginated=true`). */
export interface ContactsSearchParams {
  search?: string
  campaignId?: string
  /** C-7③ — contacts HORS de cette campagne (modale « Ajouter des contacts »). */
  excludeCampaignId?: string
  /** C-7③ — ne garder que les contacts de ce groupe… */
  group?: string
  /** …ou que les contacts SANS groupe. */
  withoutGroup?: boolean
  derivedStatus?: DerivedContactStatus
  // #208 b — filtres de la fiche enrichie (additifs).
  city?: string
  postalCode?: string
  country?: string
  cursor?: string
  /** Défaut 50, max 100 (borné par le backend). */
  limit?: number
  /** #352 — tenant désigné par le super-admin (`?tenantId=`) ; sinon le jeton fait foi. */
  tenantId?: string
}

export interface ContactsSearchResult {
  items: Contact[]
  nextCursor: string | null
  total: number
}

export class ContactsService {
  private static readonly BASE_PATH = '/contacts'

  // Get all distinct groups
  /** #352 — `tenantId` : tenant désigné par le super-admin (sinon, celui du jeton). */
  static async getGroups(tenantId?: string): Promise<string[]> {
    const url = `${ContactsService.BASE_PATH}/groups`
    const response = tenantId ? await api.get(url, { params: { tenantId } }) : await api.get(url);
    return response.data;
  }

  // Get global statistics (not just loaded contacts)
  static async getStats(campaignId?: string): Promise<ContactStats> {
    const params: any = {};
    if (campaignId) params.campaignId = campaignId;
    const response = await api.get(`${ContactsService.BASE_PATH}/stats`, { params });
    return response.data;
  }

  // Suppression en masse optimisée (1 seule requête)
  static async deleteMany(ids: string[]): Promise<{ success: boolean; deleted: number }> {
    const response = await apiWithLoading.post(
      `${ContactsService.BASE_PATH}/bulk-delete`,
      { ids },
      `Deleting ${ids.length} contact(s)...`
    );
    return response.data;
  }

  // Supprimer TOUS les contacts du tenant (ne nécessite pas de charger les contacts)
  static async deleteAll(): Promise<{ success: boolean; deleted: number }> {
    const response = await apiWithLoading.delete(
      `${ContactsService.BASE_PATH}/delete-all`,
      'Deleting all contacts...'
    );
    return response.data;
  }

  // Modification en masse
  static async updateMany(ids: string[], data: Partial<UpdateContactDto>): Promise<void> {
    await apiWithLoading.patch(`${ContactsService.BASE_PATH}/update-many`, { ids, data }, 'Modification en masse...');
  }

  // Fusion de contacts
  static async mergeContacts(ids: string[], data: Partial<UpdateContactDto>): Promise<void> {
    await apiWithLoading.post(`${ContactsService.BASE_PATH}/merge`, { ids, data }, 'Fusion des doublons...');
  }

  // Récupérer les doublons
  static async getDuplicates(): Promise<Record<string, Contact[]>> {
    const response = await apiWithLoading.get(`${ContactsService.BASE_PATH}/duplicates`, 'Recherche des doublons...');
    return response.data;
  }

  /**
   * Recherche serveur + pagination par curseur (LOT-CONTACTS-REFONTE).
   * `signal` permet d'ANNULER une requête devenue obsolète (debounce de la
   * barre de recherche) — l'appelant ignore alors l'`AbortError`.
   */
  static async search(
    params: ContactsSearchParams,
    signal?: AbortSignal,
  ): Promise<ContactsSearchResult> {
    const query: Record<string, string> = { paginated: 'true' }
    if (params.search?.trim()) query.search = params.search.trim()
    if (params.campaignId) query.campaignId = params.campaignId
    if (params.excludeCampaignId) query.excludeCampaignId = params.excludeCampaignId
    if (params.tenantId) query.tenantId = params.tenantId
    if (params.group) query.group = params.group
    if (params.withoutGroup) query.withoutGroup = 'true'
    if (params.derivedStatus) query.derivedStatus = params.derivedStatus
    if (params.city?.trim()) query.city = params.city.trim()
    if (params.postalCode?.trim()) query.postalCode = params.postalCode.trim()
    if (params.country?.trim()) query.country = params.country.trim()
    if (params.cursor) query.cursor = params.cursor
    if (params.limit !== undefined) query.limit = String(params.limit)

    const response = await api.get(ContactsService.BASE_PATH, {
      params: query,
      signal,
      headers: { 'X-Background-Request': 'true' },
    })
    return response.data
  }

  // Get all contacts
  static async getAll(campaignId?: string, offset?: number, limit?: number, status?: string, search?: string): Promise<Contact[]> {
    const params: any = {};
    if (campaignId) params.campaignId = campaignId;
    if (offset !== undefined) params.offset = offset;
    if (limit !== undefined) params.limit = limit;
    if (status && status !== 'all') params.status = status;
    if (search && search.trim().length > 0) params.search = search.trim();
    
    // Use background request to prevent loading modal during search/pagination
    const response = await api.get(ContactsService.BASE_PATH, { 
      params,
      headers: {
        'X-Background-Request': 'true'
      }
    })
    return response.data
  }

  // Get contact by ID
  static async getById(id: string): Promise<Contact> {
    // Use apiBackground to avoid loading modal during auto-dial
    const response = await apiBackground.get(
      `${ContactsService.BASE_PATH}/${id}`
    )
    return response.data
  }

  // Create new contact
  static async create(contact: CreateContactDto): Promise<Contact> {
    // ⚡ Use apiBackground to avoid loader during manual call flow
    const response = await apiBackground.post(
      ContactsService.BASE_PATH,
      contact
    )
    return response.data
  }

  // Update contact
  static async update(id: string, contact: UpdateContactDto): Promise<Contact> {
    const response = await apiWithLoading.patch(
      `${ContactsService.BASE_PATH}/${id}`,
      contact,
      'Updating contact...'
    )
    return response.data
  }

  // Delete contact
  static async delete(id: string, tenantId?: string): Promise<void> {
    const url = tenantId 
      ? `${ContactsService.BASE_PATH}/${id}?tenantId=${tenantId}`
      : `${ContactsService.BASE_PATH}/${id}`
    await apiWithLoading.delete(
      url,
      'Deleting contact...'
    )
  }

  // Bulk create contacts
  static async bulkCreate(contacts: CreateContactDto[]): Promise<Contact[]> {
    const response = await apiWithLoading.post(
      `${ContactsService.BASE_PATH}/bulk`,
      contacts,
      'Creating contacts...'
    )
    return response.data
  }

  // Get next contact to call
  static async getNextToCall(): Promise<Contact | null> {
    try {
      const response = await api.get(`${ContactsService.BASE_PATH}/next-to-call`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null // No contacts available
      }
      throw error
    }
  }

  // Reverse lookup: find contact by phone number
  static async findByPhone(phoneNumber: string): Promise<Contact | null> {
    try {
      // Use dedicated endpoint to search ONLY by phone number
      // This prevents matching contacts by name/email
      const normalizedPhone = phoneNumber.trim().replace(/\s+/g, '')
      const encodedPhone = encodeURIComponent(normalizedPhone)
      
      const response = await api.get(`${ContactsService.BASE_PATH}/find-by-phone/${encodedPhone}`, {
        headers: { 'X-Background-Request': 'true' }
      })
      
      return response.data as Contact
    } catch (error: any) {
      // 404 means no contact found - this is expected
      if (error.response?.status === 404) {
        return null
      }
      console.error('Failed to lookup contact by phone:', error)
      return null
    }
  }
}

export default ContactsService
